import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Sun, Zap, Leaf, MapPin, Menu, X, FileText, Users, Calendar, ArrowRight, 
  Building2, Shield, LogOut, Plus, Edit, Trash2, Eye, EyeOff, Mail, 
  MessageSquare, CheckCircle, LayoutDashboard, Newspaper, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_db52a624-f3b5-4135-bac8-ba469f32bf05/artifacts/o2pcw85r_IMG-20260310-WA0040.jpg";
const AERIAL_IMAGE = "https://customer-assets.emergentagent.com/job_db52a624-f3b5-4135-bac8-ba469f32bf05/artifacts/1wwzmrac_ddfafd.jpg";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Auth Context
const useAuth = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
    } catch {
      logout();
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      const newToken = response.data.token;
      localStorage.setItem('adminToken', newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error de autenticación');
    }
  };

  const logout = () => {
    if (token) {
      axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
    localStorage.removeItem('adminToken');
    setToken(null);
    setIsAuthenticated(false);
  };

  return { token, isAuthenticated, login, logout };
};

// Header Component
const Header = ({ showAdminLink = true }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Inicio" },
    { href: "/planta", label: "La Planta" },
    { href: "/noticias", label: "Noticias Socios" },
    { href: "/contacto", label: "Contacto" }
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "header-scrolled border-b border-border/40" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="MZFV Logo" className="h-14 w-14 object-contain rounded-full" />
            <span className="font-heading font-bold text-xl text-foreground hidden sm:block">MAZARRON FV</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-foreground/70 hover:text-primary font-medium transition-colors link-underline"
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              >
                {link.label}
              </a>
            ))}
            {showAdminLink && (
              <a
                href="/admin"
                className="text-muted-foreground/50 hover:text-primary text-sm transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </a>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-border/40"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-foreground/70 hover:text-primary font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </motion.nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section id="inicio" className="min-h-screen hero-gradient pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="space-y-8"
          >
            <motion.div variants={fadeInUp}>
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium tracking-wider uppercase">
                Energía Solar Fotovoltaica
              </span>
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              Central Solar <span className="gradient-text">Fotovoltaica</span> de Conexión a Red
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
              Planta de energía solar situada en <strong>Mazarrón, Murcia</strong>, formada por módulos individuales que generan energía limpia y sostenible para la red eléctrica.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href="#planta">
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 rounded-full font-medium btn-primary"
                  data-testid="cta-conocer-planta"
                >
                  Conocer la Planta
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="#contacto">
                <Button
                  variant="outline"
                  className="border-2 border-primary/20 hover:bg-primary/5 hover:border-primary px-8 py-6 rounded-full font-medium"
                  data-testid="cta-contacto"
                >
                  Contactar
                </Button>
              </a>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-5 w-5 text-primary" />
                <span>Mazarrón, Murcia</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Shield className="h-5 w-5 text-primary" />
                <span>Conexión a Red</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]">
              <img
                src={AERIAL_IMAGE}
                alt="Vista aérea de la planta fotovoltaica MAZARRON FV"
                className="w-full h-auto object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-lg hidden md:block">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Sun className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Energía</p>
                  <p className="font-heading font-semibold">100% Renovable</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About/Plant Info Section
const PlantInfo = () => {
  const features = [
    {
      icon: Sun,
      title: "Módulos Fotovoltaicos",
      description: "Instalación compuesta por paneles solares de alta eficiencia para máxima captación de energía solar."
    },
    {
      icon: Zap,
      title: "Conexión a Red",
      description: "Sistema conectado directamente a la red eléctrica, contribuyendo al suministro energético sostenible."
    },
    {
      icon: Leaf,
      title: "Energía Limpia",
      description: "Producción de energía renovable sin emisiones de CO2, contribuyendo a la lucha contra el cambio climático."
    },
    {
      icon: Building2,
      title: "Ubicación Estratégica",
      description: "Situada en Mazarrón, Murcia, zona con alta irradiación solar que maximiza la producción energética."
    }
  ];

  return (
    <section id="planta" className="py-24 md:py-32 bg-muted/30" data-testid="plant-info-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium tracking-wider uppercase mb-4">
            Nuestra Instalación
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
            Sobre la Planta
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Central solar fotovoltaica de conexión a red, formada por módulos individuales de última generación.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="feature-card h-full border-border/50 hover:border-primary/30 bg-white" data-testid={`feature-card-${index}`}>
                <CardHeader className="pb-4">
                  <div className="h-14 w-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                    <feature.icon className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="font-heading text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Technical Specs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20" data-testid="tech-specs-card">
            <CardContent className="p-8">
              <h3 className="font-heading text-2xl font-semibold mb-6 text-center">Datos Técnicos</h3>
              <div className="grid sm:grid-cols-3 gap-8 text-center">
                <div>
                  <p className="text-3xl font-heading font-bold text-primary mb-2">Por determinar</p>
                  <p className="text-muted-foreground">Potencia Instalada</p>
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold text-primary mb-2">Por determinar</p>
                  <p className="text-muted-foreground">Producción Anual</p>
                </div>
                <div>
                  <p className="text-3xl font-heading font-bold text-primary mb-2">Por determinar</p>
                  <p className="text-muted-foreground">Módulos Solares</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

// News Section - Now fetches from API
const NewsSection = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNewsItems(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      // Fallback to static data if API fails
      setNewsItems([{
        id: "1",
        date: "10 de marzo de 2026",
        title: "Convocatoria Junta General Extraordinaria",
        summary: "Se convoca a todos los socios a la Junta General Extraordinaria que se celebrará el 30 de marzo de 2026 en el Real Casino de Murcia.",
        content: `Convocatoria de Junta General Extraordinaria\n\nEmpresa: MAZARRON FV, S.L.\n\nFecha de Celebración: 30 de marzo de 2026\nHora: 18:00 horas\nLugar: Real Casino de Murcia, Calle Trapería, 18, 30001 Murcia\n\nOrden del Día:\n\n1. Modificación del artículo 7 de los Estatutos Sociales relativo a la Junta General, convocatoria, modo de celebración y mayorías necesarias para la adopción de acuerdos.\n\n2. Creación de la página web corporativa de conformidad con el Art. 11.bis de la LSC.\n\n3. Delegación de facultades para la elevación a escritura pública del acuerdo adoptado y su inscripción en el Registro Mercantil.\n\n4. Ruegos y preguntas.\n\n5. Designación de dos interventores de acta.`,
        news_type: "convocatoria",
        pdf_url: "https://customer-assets.emergentagent.com/job_db52a624-f3b5-4135-bac8-ba469f32bf05/artifacts/0imi479i_Comparto%20%27CONVOCATORIA%20JUNTA%20GENERAL%27%20con%20usted.pdf"
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="noticias" className="py-24 md:py-32" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-secondary/10 text-secondary rounded-full text-sm font-medium tracking-wider uppercase mb-4">
            Comunicaciones
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
            Noticias Socios
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Información relevante y comunicados oficiales para los socios de MAZARRON FV, S.L.
          </motion.p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : newsItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No hay noticias publicadas actualmente.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newsItems.map((news, index) => (
              <motion.div
                key={news.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Dialog>
                  <DialogTrigger asChild>
                    <Card 
                      className="news-card cursor-pointer h-full border-border/50 hover:border-secondary/30 hover:shadow-lg transition-all bg-white"
                      data-testid={`news-card-${news.id}`}
                    >
                      <CardHeader>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Calendar className="h-4 w-4" />
                          <span>{news.date}</span>
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium w-fit mb-3">
                          <Users className="h-3 w-3" />
                          {news.news_type === "convocatoria" ? "Convocatoria" : "Noticia"}
                        </div>
                        <CardTitle className="font-heading text-lg news-title transition-colors">
                          {news.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-4">{news.summary}</p>
                        <span className="inline-flex items-center text-primary font-medium text-sm">
                          Leer más <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </CardContent>
                    </Card>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="news-dialog">
                    <DialogHeader>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Calendar className="h-4 w-4" />
                        <span>{news.date}</span>
                      </div>
                      <DialogTitle className="font-heading text-2xl">{news.title}</DialogTitle>
                      <DialogDescription className="sr-only">
                        Detalles de la noticia {news.title}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 space-y-4">
                      <div className="prose prose-sm max-w-none">
                        {news.content.split('\n').map((line, i) => (
                          <p key={i} className={`${line.startsWith('**') ? 'font-semibold' : ''} text-foreground/80 leading-relaxed`}>
                            {line.replace(/\*\*/g, '')}
                          </p>
                        ))}
                      </div>
                      {news.pdf_url && (
                        <a
                          href={news.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors mt-6"
                          data-testid="download-pdf-btn"
                        >
                          <FileText className="h-5 w-5" />
                          Descargar Documento PDF
                        </a>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Mensaje enviado correctamente", {
        description: "Nos pondremos en contacto contigo pronto."
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Error al enviar el mensaje", {
        description: "Por favor, inténtalo de nuevo más tarde."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contacto" className="py-24 md:py-32 bg-muted/30" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium tracking-wider uppercase mb-4">
            Contacto
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
            Ponte en Contacto
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            ¿Tienes alguna pregunta o necesitas más información? Estamos aquí para ayudarte.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-white border-border/50" data-testid="contact-form-card">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">Nombre completo</label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      required
                      className="h-12 rounded-xl"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">Correo electrónico</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      required
                      className="h-12 rounded-xl"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">Teléfono (opcional)</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+34 XXX XXX XXX"
                      className="h-12 rounded-xl"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">Mensaje</label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Escribe tu mensaje aquí..."
                      required
                      rows={5}
                      className="rounded-xl resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-medium btn-primary"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-heading text-2xl font-semibold mb-6">Información de Contacto</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Ubicación de la Planta</p>
                    <p className="text-muted-foreground">Mazarrón, Murcia, España</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Domicilio Social</p>
                    <p className="text-muted-foreground">Avenida Antonete Galvez 14º C<br/>30006, Murcia (Murcia), España</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-64 bg-muted">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d51100.33753839599!2d-1.5034762!3d37.5989351!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd63e3d8e3c8b5d1%3A0x8c78d8f6a8b5e4c2!2sMazarr%C3%B3n%2C%20Murcia!5e0!3m2!1ses!2ses!4v1710000000000!5m2!1ses!2ses"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Mazarrón"
                data-testid="contact-map"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-foreground text-background py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="MZFV Logo" className="h-14 w-14 object-contain rounded-full bg-white p-1" />
              <span className="font-heading font-bold text-2xl">MAZARRON FV</span>
            </div>
            <p className="text-background/70 leading-relaxed max-w-md">
              Central solar fotovoltaica de conexión a red, formada por módulos individuales. 
              Generando energía limpia y sostenible en Mazarrón, Murcia.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Enlaces</h4>
            <ul className="space-y-3">
              <li><a href="/" className="text-background/70 hover:text-primary transition-colors">Inicio</a></li>
              <li><a href="/planta" className="text-background/70 hover:text-primary transition-colors">La Planta</a></li>
              <li><a href="/noticias" className="text-background/70 hover:text-primary transition-colors">Noticias Socios</a></li>
              <li><a href="/contacto" className="text-background/70 hover:text-primary transition-colors">Contacto</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Contacto</h4>
            <ul className="space-y-3 text-background/70">
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Mazarrón, Murcia
              </li>
            </ul>
          </div>
        </div>

        {/* Company Info */}
        <div className="border-t border-background/10 pt-8">
          <div className="bg-background/5 rounded-2xl p-6 mb-8">
            <h4 className="font-heading font-semibold text-lg mb-4 text-center">Datos de la Sociedad</h4>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-background/50 text-sm mb-1">RAZÓN SOCIAL</p>
                <p className="font-medium">MAZARRON FV SL</p>
              </div>
              <div>
                <p className="text-background/50 text-sm mb-1">NIF</p>
                <p className="font-medium">B73334773</p>
              </div>
              <div>
                <p className="text-background/50 text-sm mb-1">FORMA JURÍDICA</p>
                <p className="font-medium">SOCIEDAD LIMITADA</p>
              </div>
              <div>
                <p className="text-background/50 text-sm mb-1">DOMICILIO</p>
                <p className="font-medium text-sm">Avenida Antonete Galvez 14º C. 30006, Murcia (Murcia). España</p>
              </div>
            </div>
          </div>

          <div className="text-center text-background/50 text-sm">
            <p>© {new Date().getFullYear()} MAZARRON FV SL. Todos los derechos reservados.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// ADMIN PANEL COMPONENTS
// ============================================

// Admin Login Page
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await onLogin(username, password);
      toast.success("Sesión iniciada correctamente");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <Card className="w-full max-w-md" data-testid="admin-login-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="MZFV Logo" className="h-16 w-16 object-contain rounded-full" />
          </div>
          <CardTitle className="font-heading text-2xl">Panel de Administración</CardTitle>
          <CardDescription>Accede para gestionar noticias y convocatorias</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Usuario</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                required
                className="mt-1"
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="admin-login-btn"
            >
              {isLoading ? "Iniciando sesión..." : "Iniciar Sesión"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-muted-foreground hover:text-primary">
              ← Volver a la web
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ token, onLogout }) => {
  const [newsItems, setNewsItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNews, setEditingNews] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [newsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/news`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers })
      ]);
      setNewsItems(newsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        onLogout();
        navigate("/admin");
      }
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNews = async (data) => {
    try {
      await axios.post(`${API}/admin/news`, data, { headers });
      toast.success("Noticia creada correctamente");
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error("Error al crear la noticia");
    }
  };

  const handleUpdateNews = async (id, data) => {
    try {
      await axios.put(`${API}/admin/news/${id}`, data, { headers });
      toast.success("Noticia actualizada correctamente");
      setEditingNews(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar la noticia");
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta noticia?")) return;
    
    try {
      await axios.delete(`${API}/admin/news/${id}`, { headers });
      toast.success("Noticia eliminada correctamente");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar la noticia");
    }
  };

  const handleTogglePublished = async (news) => {
    try {
      await axios.put(`${API}/admin/news/${news.id}`, { published: !news.published }, { headers });
      toast.success(news.published ? "Noticia despublicada" : "Noticia publicada");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar la noticia");
    }
  };

  const handleMarkRead = async (contactId) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/read`, {}, { headers });
      fetchData();
    } catch (error) {
      toast.error("Error al marcar como leído");
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate("/admin");
  };

  const unreadContacts = contacts.filter(c => !c.read).length;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Admin Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MZFV Logo" className="h-10 w-10 object-contain rounded-full" />
            <div>
              <h1 className="font-heading font-bold text-lg">Panel de Administración</h1>
              <p className="text-xs text-muted-foreground">MAZARRON FV SL</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-muted-foreground hover:text-primary" target="_blank">
              Ver web →
            </a>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="news" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="news" className="flex items-center gap-2" data-testid="admin-tab-news">
              <Newspaper className="h-4 w-4" />
              Noticias
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2" data-testid="admin-tab-contacts">
              <MessageSquare className="h-4 w-4" />
              Mensajes
              {unreadContacts > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {unreadContacts}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* News Tab */}
          <TabsContent value="news" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-semibold">Gestión de Noticias</h2>
              <Dialog open={isCreating} onOpenChange={setIsCreating}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-news-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Noticia
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Crear Nueva Noticia</DialogTitle>
                    <DialogDescription>
                      Completa los campos para crear una nueva noticia o convocatoria.
                    </DialogDescription>
                  </DialogHeader>
                  <NewsForm onSubmit={handleCreateNews} onCancel={() => setIsCreating(false)} />
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : newsItems.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Newspaper className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay noticias creadas.</p>
                  <p className="text-sm text-muted-foreground">Crea tu primera noticia con el botón de arriba.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {newsItems.map((news) => (
                  <Card key={news.id} className="overflow-hidden" data-testid={`admin-news-item-${news.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={news.news_type === "convocatoria" ? "default" : "secondary"}>
                              {news.news_type === "convocatoria" ? "Convocatoria" : "Noticia"}
                            </Badge>
                            <Badge variant={news.published ? "outline" : "destructive"}>
                              {news.published ? "Publicada" : "Borrador"}
                            </Badge>
                            <span className="text-sm text-muted-foreground">{news.date}</span>
                          </div>
                          <h3 className="font-heading font-semibold text-lg mb-2 truncate">{news.title}</h3>
                          <p className="text-muted-foreground text-sm line-clamp-2">{news.summary}</p>
                          {news.pdf_url && (
                            <div className="flex items-center gap-1 mt-2 text-sm text-primary">
                              <FileText className="h-4 w-4" />
                              <span>PDF adjunto</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleTogglePublished(news)}
                            title={news.published ? "Despublicar" : "Publicar"}
                            data-testid={`toggle-publish-${news.id}`}
                          >
                            {news.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Dialog open={editingNews?.id === news.id} onOpenChange={(open) => !open && setEditingNews(null)}>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditingNews(news)}
                                data-testid={`edit-news-${news.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Editar Noticia</DialogTitle>
                                <DialogDescription>
                                  Modifica los campos necesarios.
                                </DialogDescription>
                              </DialogHeader>
                              <NewsForm
                                initialData={news}
                                onSubmit={(data) => handleUpdateNews(news.id, data)}
                                onCancel={() => setEditingNews(null)}
                              />
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteNews(news.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-news-${news.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-6">
            <h2 className="font-heading text-2xl font-semibold">Mensajes de Contacto</h2>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : contacts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay mensajes de contacto.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <Card 
                    key={contact.id} 
                    className={`overflow-hidden ${!contact.read ? 'border-primary/50 bg-primary/5' : ''}`}
                    data-testid={`admin-contact-${contact.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {!contact.read && (
                              <Badge variant="default" className="bg-primary">Nuevo</Badge>
                            )}
                            <span className="text-sm text-muted-foreground">
                              {new Date(contact.timestamp).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg mb-1">{contact.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Mail className="h-4 w-4" />
                              {contact.email}
                            </span>
                            {contact.phone && (
                              <span>{contact.phone}</span>
                            )}
                          </div>
                          <p className="text-foreground/80 whitespace-pre-wrap">{contact.message}</p>
                        </div>
                        {!contact.read && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkRead(contact.id)}
                            data-testid={`mark-read-${contact.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Marcar leído
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// News Form Component
const NewsForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    summary: initialData?.summary || "",
    content: initialData?.content || "",
    date: initialData?.date || "",
    news_type: initialData?.news_type || "noticia",
    pdf_url: initialData?.pdf_url || "",
    published: initialData?.published ?? true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 sm:col-span-1">
          <Label htmlFor="news_type">Tipo</Label>
          <Select value={formData.news_type} onValueChange={(v) => handleChange("news_type", v)}>
            <SelectTrigger className="mt-1" data-testid="news-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="noticia">Noticia</SelectItem>
              <SelectItem value="convocatoria">Convocatoria</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <Label htmlFor="date">Fecha de publicación</Label>
          <Input
            id="date"
            value={formData.date}
            onChange={(e) => handleChange("date", e.target.value)}
            placeholder="Ej: 10 de marzo de 2026"
            required
            className="mt-1"
            data-testid="news-date-input"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="title">Título</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          placeholder="Título de la noticia"
          required
          className="mt-1"
          data-testid="news-title-input"
        />
      </div>

      <div>
        <Label htmlFor="summary">Resumen</Label>
        <Textarea
          id="summary"
          value={formData.summary}
          onChange={(e) => handleChange("summary", e.target.value)}
          placeholder="Breve resumen que aparece en la tarjeta"
          required
          rows={2}
          className="mt-1"
          data-testid="news-summary-input"
        />
      </div>

      <div>
        <Label htmlFor="content">Contenido completo</Label>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) => handleChange("content", e.target.value)}
          placeholder="Contenido completo de la noticia..."
          required
          rows={8}
          className="mt-1"
          data-testid="news-content-input"
        />
      </div>

      <div>
        <Label htmlFor="pdf_url">URL del PDF (opcional)</Label>
        <Input
          id="pdf_url"
          value={formData.pdf_url}
          onChange={(e) => handleChange("pdf_url", e.target.value)}
          placeholder="https://..."
          className="mt-1"
          data-testid="news-pdf-input"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch
            id="published"
            checked={formData.published}
            onCheckedChange={(v) => handleChange("published", v)}
            data-testid="news-published-switch"
          />
          <Label htmlFor="published">Publicar inmediatamente</Label>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground" data-testid="news-submit-btn">
          {isSubmitting ? "Guardando..." : (initialData ? "Actualizar" : "Crear Noticia")}
        </Button>
      </DialogFooter>
    </form>
  );
};

// Home Page Component
const HomePage = ({ scrollToSection }) => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to section based on prop or URL path
    const section = scrollToSection || location.pathname.replace('/', '');
    
    if (section && section !== '') {
      // Small delay to ensure the page is rendered
      setTimeout(() => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [scrollToSection, location.pathname]);

  return (
    <>
      <Header />
      <main>
        <Hero />
        <PlantInfo />
        <NewsSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

// Admin Router Component
const AdminRouter = ({ auth }) => {
  if (!auth.isAuthenticated) {
    return <AdminLogin onLogin={auth.login} />;
  }
  return <AdminDashboard token={auth.token} onLogout={auth.logout} />;
};

// Main App Component
function App() {
  const auth = useAuth();

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/noticias" element={<HomePage scrollToSection="noticias" />} />
          <Route path="/planta" element={<HomePage scrollToSection="planta" />} />
          <Route path="/contacto" element={<HomePage scrollToSection="contacto" />} />
          <Route path="/admin/*" element={<AdminRouter auth={auth} />} />
          <Route path="/admin/dashboard" element={
            auth.isAuthenticated ? 
              <AdminDashboard token={auth.token} onLogout={auth.logout} /> : 
              <Navigate to="/admin" replace />
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
